import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Check, MessageSquare, Calendar, CheckCircle2, Clock, AlertCircle, PlayCircle, ChevronUp } from 'lucide-react';
import api from '../api';
import ReviewPlayer from '../components/ReviewPlayer';

const FMT_DOT = {
  'Reel':'#a855f7', 'Carosello':'#00d4ff', 'Storia':'#f59e0b', 'Post Singolo':'#10b981',
  'Articolo SEO':'#3b82f6', 'Video YT':'#f43f5e', 'Newsletter':'#14b8a6',
};
const APPRO = {
  approvato:  { label:'Approvato',          color:'var(--green)',  bg:'var(--green-dim)',  Icon:CheckCircle2 },
  modifiche:  { label:'Modifiche richieste',color:'var(--yellow)', bg:'var(--yellow-dim)', Icon:AlertCircle },
  in_attesa:  { label:'In attesa',          color:'var(--text-muted)', bg:'var(--bg3)',    Icon:Clock },
};

function fmtData(d) {
  if (!d) return 'Data da definire';
  return new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { weekday:'short', day:'numeric', month:'long' });
}

export default function ClientPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | error
  const [openFeedback, setOpenFeedback] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [busy, setBusy] = useState(null);
  const [tab, setTab] = useState('approvazioni');
  const [aperto, setAperto] = useState(null);   // id contenuto con il player aperto

  const load = () => {
    api.get(`/portal/${token}`, { silent: true })
      .then(r => { setData(r.data); setStatus('ok'); })
      .catch(() => setStatus('error'));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const decidi = async (item, stato, feedback) => {
    setBusy(item.id);
    try {
      await api.post(`/portal/${token}/approva/${item.id}`, { stato, feedback: feedback || null }, { silent: true });
      setData(d => ({ ...d, contenuti: d.contenuti.map(c => c.id === item.id ? { ...c, approvazione: stato, feedback: feedback || null } : c) }));
      setOpenFeedback(null); setFeedbackText('');
    } catch { /* ignora */ }
    setBusy(null);
  };

  if (status === 'loading') {
    return <Shell><div style={{ padding:60, textAlign:'center', color:'var(--text-dim)' }}>Caricamento…</div></Shell>;
  }
  if (status === 'error') {
    return <Shell><div style={{ padding:60, textAlign:'center', color:'var(--text-muted)' }}>
      <AlertCircle size={32} style={{ opacity:0.4, marginBottom:12 }}/>
      <div style={{ fontSize:15 }}>Link non valido o non più attivo.</div>
      <div style={{ fontSize:13, color:'var(--text-dim)', marginTop:6 }}>Contatta la tua agenzia per un nuovo link.</div>
    </div></Shell>;
  }

  const contenuti = data?.contenuti || [];
  const daApprovare = contenuti.filter(c => !c.approvazione || c.approvazione === 'in_attesa').length;

  return (
    <Shell>
      <div style={{ marginBottom:6, fontSize:12, color:'var(--cyan)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em' }}>Area cliente</div>
      <h1 style={{ fontSize:24, fontWeight:700, color:'var(--text)', margin:'0 0 14px' }}>{data?.cliente}</h1>

      {/* Tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:20, borderBottom:'1px solid var(--border)' }}>
        {[{k:'approvazioni',l:'Approvazioni'},{k:'report',l:'Report mensile'}].map(t => (
          <button key={t.k} onClick={()=>setTab(t.k)}
            style={{ background:'none', border:'none', cursor:'pointer', fontSize:13.5, fontWeight:600, padding:'8px 12px',
              color: tab===t.k ? 'var(--cyan)' : 'var(--text-muted)',
              borderBottom: `2px solid ${tab===t.k ? 'var(--cyan)' : 'transparent'}`, marginBottom:-1 }}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'report' ? <PortalReport token={token} /> : (
      <>
      <p style={{ fontSize:13.5, color:'var(--text-muted)', margin:'0 0 24px' }}>
        {daApprovare > 0
          ? `${daApprovare} content${daApprovare===1?'o':'i'} in attesa della tua approvazione.`
          : 'Tutti i contenuti sono stati revisionati. Grazie!'}
      </p>

      {contenuti.length === 0 ? (
        <div style={{ padding:48, textAlign:'center', color:'var(--text-dim)', border:'1px dashed var(--border)', borderRadius:12 }}>
          Nessun contenuto da approvare al momento.
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {contenuti.map(item => {
            const ap = APPRO[item.approvazione] || APPRO.in_attesa;
            const deciso = item.approvazione === 'approvato' || item.approvazione === 'modifiche';
            return (
              <div key={item.id} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:14, padding:'16px 18px' }}>
                <div style={{ display:'flex', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
                  <span style={{ width:9, height:9, borderRadius:'50%', background:FMT_DOT[item.formato]||'var(--text-dim)', marginTop:6, flexShrink:0 }}/>
                  <div style={{ flex:1, minWidth:200 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:3 }}>
                      {item.id_post && <span style={{ fontSize:11, fontFamily:'monospace', fontWeight:600, color:'var(--cyan)', background:'var(--cyan-dim)', padding:'1px 6px', borderRadius:5 }}>{item.id_post}</span>}
                      <span style={{ fontSize:11, color:'var(--text-dim)' }}>{item.formato} · {item.piattaforma}</span>
                    </div>
                    <div style={{ fontSize:15, fontWeight:600, color:'var(--text)' }}>{item.titolo_post}</div>
                    {item.hook && <div style={{ fontSize:13, color:'var(--text-muted)', marginTop:2 }}>"{item.hook}"</div>}
                    {item.caption_pubblica && aperto !== item.id && (
                      <div style={{ fontSize:12.5, color:'var(--text-muted)', marginTop:6, whiteSpace:'pre-wrap', display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{item.caption_pubblica}</div>
                    )}
                    <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:12, color:'var(--text-dim)', marginTop:8 }}>
                      <Calendar size={12}/> {fmtData(item.data_pubblicazione)}
                    </div>
                    {item.feedback && (
                      <div style={{ fontSize:12.5, color:'var(--text-muted)', marginTop:8, background:'var(--bg3)', borderRadius:8, padding:'8px 10px' }}>
                        <b style={{ color:'var(--text)' }}>Tuo feedback:</b> {item.feedback}
                      </div>
                    )}
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:8, flexShrink:0 }}>
                    <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:11.5, fontWeight:600, color:ap.color, background:ap.bg, padding:'4px 10px', borderRadius:999 }}>
                      <ap.Icon size={12}/> {ap.label}
                    </span>
                    {(item.media || []).length > 0 && aperto !== item.id && (
                      <button onClick={() => setAperto(item.id)} title="Guarda e commenta"
                        style={{ position:'relative', width:92, height:92, borderRadius:10, overflow:'hidden', border:'1px solid var(--border)', background:'var(--bg3)', padding:0, cursor:'pointer' }}>
                        {item.media[0].thumb || !item.media[0].video
                          ? <img src={item.media[0].thumb || item.media[0].url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                          : <video src={item.media[0].url} preload="metadata" muted style={{ width:'100%', height:'100%', objectFit:'cover' }}/>}
                        {item.media[0].video && <PlayCircle size={28} color="#fff" style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', filter:'drop-shadow(0 1px 3px rgba(0,0,0,0.6))' }}/>}
                      </button>
                    )}
                  </div>
                </div>

                {aperto === item.id && (
                  <PortalReview token={token} item={item} onClose={() => setAperto(null)}
                    onCommento={() => setData(d => ({ ...d, contenuti: d.contenuti.map(c => c.id === item.id ? { ...c, commenti: Number(c.commenti || 0) + 1 } : c) }))} />
                )}

                {/* Azioni */}
                {openFeedback === item.id ? (
                  <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:8 }}>
                    <textarea value={feedbackText} onChange={e => setFeedbackText(e.target.value)} autoFocus
                      placeholder="Cosa vorresti modificare?"
                      style={{ width:'100%', boxSizing:'border-box', minHeight:70, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', fontSize:13, padding:'9px 11px', resize:'vertical', outline:'none', fontFamily:'inherit' }}/>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={() => decidi(item, 'modifiche', feedbackText)} disabled={busy===item.id}
                        style={{ background:'var(--yellow)', color:'#1a1200', border:'none', borderRadius:8, padding:'8px 14px', fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
                        Invia richiesta
                      </button>
                      <button onClick={() => { setOpenFeedback(null); setFeedbackText(''); }}
                        style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-muted)', padding:'8px 14px', fontSize:12.5, cursor:'pointer' }}>
                        Annulla
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
                    <button onClick={() => decidi(item, 'approvato')} disabled={busy===item.id}
                      style={{ display:'inline-flex', alignItems:'center', gap:6, background: deciso&&item.approvazione==='approvato' ? 'var(--green-dim)' : 'var(--green)', color: deciso&&item.approvazione==='approvato' ? 'var(--green)' : '#001a10', border: deciso&&item.approvazione==='approvato'?'1px solid var(--green)':'none', borderRadius:8, padding:'8px 16px', fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
                      <Check size={14}/> Approva
                    </button>
                    <button onClick={() => { setOpenFeedback(item.id); setFeedbackText(item.feedback || ''); }}
                      style={{ display:'inline-flex', alignItems:'center', gap:6, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-muted)', padding:'8px 16px', fontSize:12.5, fontWeight:500, cursor:'pointer' }}>
                      <MessageSquare size={14}/> Richiedi modifiche
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
    </Shell>
  );
}

function PortalReview({ token, item, onClose, onCommento }) {
  const [commenti, setCommenti] = useState([]);
  const [idx, setIdx] = useState(0);
  const [nome, setNome] = useState(() => { try { return localStorage.getItem('eden-portal-nome') || ''; } catch { return ''; } });
  useEffect(() => {
    api.get(`/portal/${token}/contenuto/${item.id}/commenti`, { silent: true }).then(r => setCommenti(r.data || [])).catch(() => {});
  }, [token, item.id]);
  const media = item.media || [];
  const add = async (testo, tc) => {
    try { localStorage.setItem('eden-portal-nome', nome); } catch { /* storage non disponibile */ }
    const r = await api.post(`/portal/${token}/contenuto/${item.id}/commenti`,
      { testo, timecode_sec: tc, versione_id: item.versione_id || null, autore_nome: nome || null }, { silent: true });
    setCommenti(cs => [...cs, r.data]);
    onCommento && onCommento();
  };
  return (
    <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid var(--border)' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, flexWrap:'wrap' }}>
        {item.versione_numero && <span style={{ fontSize:11.5, fontWeight:700, color:'var(--cyan)', background:'var(--cyan-dim)', padding:'2px 8px', borderRadius:6 }}>Versione {item.versione_numero}</span>}
        {media.length > 1 && media.map((m, i) => (
          <button key={i} onClick={() => setIdx(i)} style={{ fontSize:11.5, padding:'2px 8px', borderRadius:6, cursor:'pointer', border:`1px solid ${i === idx ? 'var(--cyan)' : 'var(--border)'}`, background:'var(--surface)', color: i === idx ? 'var(--cyan)' : 'var(--text-muted)' }}>{i + 1}</button>
        ))}
        <input value={nome} onChange={e => setNome(e.target.value)} placeholder="Il tuo nome"
          style={{ marginLeft:'auto', width:150, background:'var(--bg3)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', fontSize:12.5, padding:'6px 9px', outline:'none' }}/>
        <button onClick={onClose} title="Chiudi" style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-dim)', display:'flex' }}><ChevronUp size={18}/></button>
      </div>
      {item.caption_pubblica && (
        <div style={{ fontSize:12.5, color:'var(--text-muted)', whiteSpace:'pre-wrap', background:'var(--bg3)', borderRadius:8, padding:'8px 10px', marginBottom:10 }}>{item.caption_pubblica}</div>
      )}
      <ReviewPlayer compact media={media[idx]} commenti={commenti} onAdd={add}
        placeholder="Metti in pausa il video e scrivi cosa cambiare in quel punto…" />
    </div>
  );
}

const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

function PortalReport({ token }) {
  const [rep, setRep] = useState(null);
  const [status, setStatus] = useState('loading');
  const now = new Date();
  const [mese, setMese] = useState(now.getMonth() + 1);
  const [anno, setAnno] = useState(now.getFullYear());

  useEffect(() => {
    setStatus('loading');
    api.get(`/portal/${token}/report`, { params: { mese, anno }, silent: true })
      .then(r => { setRep(r.data); setStatus('ok'); })
      .catch(() => setStatus('error'));
  }, [token, mese, anno]);

  const shift = (d) => { let m = mese + d, a = anno; if (m < 1) { m = 12; a--; } if (m > 12) { m = 1; a++; } setMese(m); setAnno(a); };

  if (status === 'loading') return <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)' }}>Caricamento…</div>;
  if (status === 'error') return <div style={{ padding:40, textAlign:'center', color:'var(--text-muted)' }}>Report non disponibile.</div>;

  const t = rep?.totali || {};
  const cont = rep?.contenuti || [];
  return (
    <div>
      {/* selettore mese */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:14, marginBottom:20 }}>
        <button onClick={() => shift(-1)} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, cursor:'pointer', color:'var(--text-muted)', width:32, height:32 }}>‹</button>
        <div style={{ fontSize:15, fontWeight:600, color:'var(--text)', minWidth:140, textAlign:'center' }}>{MESI[mese-1]} {anno}</div>
        <button onClick={() => shift(1)} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:8, cursor:'pointer', color:'var(--text-muted)', width:32, height:32 }}>›</button>
      </div>

      {/* KPI */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))', gap:10, marginBottom:18 }}>
        {[['Pubblicati', t.pubblicati], ['In piano', t.pianificati], ['Impression', t.impressions]].map(([l, v]) => (
          <div key={l} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:12, padding:'14px 16px' }}>
            <div style={{ fontSize:24, fontWeight:700, color:'var(--cyan)' }}>{Number(v || 0).toLocaleString('it-IT')}</div>
            <div style={{ fontSize:11, color:'var(--text-dim)' }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Canali / follower */}
      {(rep?.canali || []).length > 0 && (
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:18 }}>
          {rep.canali.map((c, i) => {
            const foll = (c.metrics || {}).followers ?? (c.metrics || {}).follower_count;
            return (
              <div key={i} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'8px 14px', fontSize:12.5 }}>
                <span style={{ textTransform:'capitalize', color:'var(--text-muted)' }}>{c.source}</span>{foll != null && <b style={{ color:'var(--text)', marginLeft:6 }}>{Number(foll).toLocaleString('it-IT')} follower</b>}
              </div>
            );
          })}
        </div>
      )}

      {/* Lista contenuti del mese */}
      {cont.length === 0 ? (
        <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)', border:'1px dashed var(--border)', borderRadius:12 }}>Nessun contenuto in questo mese.</div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {cont.map((x, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:10, background:'var(--surface)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 14px' }}>
              <span style={{ width:8, height:8, borderRadius:'50%', background: FMT_DOT[x.formato] || 'var(--text-dim)', flexShrink:0 }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:500, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{x.titolo_post}</div>
                <div style={{ fontSize:11, color:'var(--text-dim)' }}>{x.formato} · {x.data_pubblicazione ? new Date(x.data_pubblicazione+'T00:00:00').toLocaleDateString('it-IT',{day:'numeric',month:'short'}) : ''}</div>
              </div>
              {x.pub_stato === 'published' && <CheckCircle2 size={14} color="var(--green)"/>}
              {x.insights && x.insights.impressions != null && <span style={{ fontSize:12, color:'var(--cyan)' }}>{Number(x.insights.impressions).toLocaleString('it-IT')} impr.</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Shell({ children }) {
  return (
    <div style={{ minHeight:'100dvh', background:'var(--bg)', padding:'clamp(20px,5vw,48px) clamp(16px,4vw,24px)' }}>
      <div style={{ maxWidth:680, margin:'0 auto' }}>
        {children}
        <div style={{ textAlign:'center', marginTop:32, fontSize:11, color:'var(--text-dim)' }}>
          Powered by EdenFlow
        </div>
      </div>
    </div>
  );
}
